/**
 * A row the grid is not holding.
 *
 * A window is a contiguous slice of a view, so a link to row five thousand
 * arrives with nothing on screen anywhere near it. The row is fetched by id and
 * put into **a window of its own** -- a real `RowWindowStore` holding exactly
 * one row.
 *
 * That is the whole trick, and it is why there is no second implementation of
 * anything here. Everything that writes to a row -- the optimistic apply, the
 * rollback, the reconciliation with what the server made of the edit, the
 * per-row queue -- speaks to a `RowWindowMember`, and a member is a store and
 * some questions about a view. Give the modal one of those and every path it
 * needs already exists. The alternative is a copy of the row in component state
 * and a second, smaller optimism beside the first, which would agree with it
 * until it did not.
 *
 * The window is deliberately **not registered** with the realtime layer. A
 * registered window is one that row events may insert into, and this one holds
 * a single named row and has no positions for anything else to go at. The cost
 * is that somebody else's edit to a deep-linked row does not arrive until the
 * page is reloaded; the alternative is teaching the registry about a kind of
 * window that only ever accepts updates, for a case that lasts as long as a
 * dialog is open.
 */
import { useEffect, useRef, useState } from 'react'
import { useStore } from 'zustand'

import type { Field, RowData } from '@/api/types'
import type { QueryableView } from '../rows/rowQuery'
import { resetWindow } from '../rows/rowWindow'
import { createRowWindowStore, type RowWindowStore } from '../rows/rowWindowStore'
import type { RowWindowMember } from '../rows/rowWindowRegistry'
import { fetchRow } from './requests'

/**
 * The signature a detached window carries.
 *
 * Never compared against a real one: this window is never pointed at a query,
 * so nothing ever asks whether its contents still answer the question that
 * fetched them.
 */
const DETACHED = 'detached'

export interface DetachedRow {
  member: RowWindowMember
  row: RowData | null
  /** True once the fetch has failed: the row is gone, or was never readable. */
  missing: boolean
}

export function useDetachedRow({
  tableId,
  viewId,
  rowId,
  fields,
  view,
}: {
  tableId: number
  viewId: number
  /** The row to hold, or null when the grid is holding it already. */
  rowId: number | null
  fields: readonly Field[]
  view: QueryableView
}): DetachedRow {
  const [store] = useState<RowWindowStore>(() =>
    createRowWindowStore({
      // A window that never asks for a range. It is told what it holds.
      fetchRows: () => Promise.reject(new Error('a detached row does not fetch ranges')),
    }),
  )

  const latest = useRef({ fields, view })
  latest.current = { fields, view }

  const [member] = useState<RowWindowMember>(() => ({
    tableId,
    viewId,
    store,
    fields: () => latest.current.fields,
    view: () => latest.current.view,
    groupBys: () => latest.current.view.group_bys ?? [],
  }))

  const [missing, setMissing] = useState(false)
  const window = useStore(store, (state) => state.window)

  useEffect(() => {
    if (rowId === null) return

    setMissing(false)
    let current = true

    void fetchRow(tableId, rowId)
      .then((row) => {
        // The answer to a question nobody is asking any more: the dialog has
        // stepped on to another row since this was sent. Without the check, a
        // slow answer arriving after a fast one puts the row somebody has
        // already moved off back on the screen.
        if (!current) return
        // The whole window, not an insertion. A window that holds one named
        // row cannot hold two, and replacing it is the only way to say that
        // which does not depend on having taken the last one out first.
        store.setState({ window: resetWindow([row], 0, 1, DETACHED) })
      })
      .catch(() => {
        if (current) setMissing(true)
      })

    return () => {
      current = false
    }
  }, [store, tableId, rowId])

  return {
    member,
    row: rowId === null ? null : (window.rows.find((row) => row.id === rowId) ?? null),
    missing,
  }
}
